import React, {useEffect, useMemo, useState} from 'react';
import {FileError, FileRejection, useDropzone} from 'react-dropzone';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {FormikValues, useFormikContext} from 'formik';
import colors from '../../util/colors';
import {Icons} from '../../util/icons';
import {strings} from '../../util/strings';
import {createGUID} from '../../util/utility';
import AppButton from '../app-button';
import {AppFormCheckboxField} from '../forms';
import {Column, Row} from '../layout';
import AppFilePreview from './app-file-preview';
import attachfilesgrey from '../../assets/attach-files-grey.svg';
import * as fonts from '../../util/fonts';
import {useMediaQuery} from 'react-responsive';

import './styles.scss';
import NewErrorMessage from '../new-error-message';

interface AppDragDropFileInputProps {
  files: any[];
  setFiles(values: any[]): void;
  labels: typeof strings.pt.createRefund;
  labelsMessages: typeof strings.pt.messages;
  checkbox?: boolean;
  checkboxPreRefund?: boolean;
  hidePreviews?: boolean;
  preAuth?: boolean;
  newFilesName?: string;
  onFileAdd?(files: any): void;
  customResponsiveButton?(): void;
  customOnDropBehavior?: VoidFunction;
  name?: any;
  filesSizeLimit?: boolean;
  globalStyles: any;
  submitCount?: number;
}

function AppDragDropFileInput({
  files,
  setFiles,
  labels,
  labelsMessages,
  checkbox = false,
  checkboxPreRefund = false,
  hidePreviews = false,
  newFilesName = '',
  onFileAdd,
  customResponsiveButton,
  customOnDropBehavior,
  preAuth,
  filesSizeLimit,
  globalStyles,
  submitCount,
  name = 'noDocuments',
}: AppDragDropFileInputProps) {
  const [uploadedFiles, setUploadedFiles] = useState(files);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasError, setError] = useState<boolean>(false);

  const isResponsive = useMediaQuery({maxDeviceWidth: 768});

  // Get formik context to track checkbox state
  const formik = useFormikContext<FormikValues>();
  const isCheckboxChecked = formik?.values?.[name] || false;

  const createRefundLabels = {
    ...strings.pt.createRefund,
    ...labels,
  };

  const messagesLabels = {
    ...strings.pt.messages,
    ...labelsMessages,
  };

  const maxSize = 10485760;
  const [totalSize, setTotalSize] = useState(0);

  // permite letras (incl. acentos), dígitos, espaço, hífen e underscore + extensão 1–10
  const FILENAME_REGEX = /^[\p{L}\p{M}\p{N} _-]+\.[A-Za-z0-9]{1,10}$/u;

  function isValidFileName(value?: string | null): boolean {
    if (!value) return false;
    const nfc = value.normalize('NFC').trim(); // junta "e"+"´" → "é"
    return FILENAME_REGEX.test(nfc);
  }

  // Clear files when checkbox is checked
  useEffect(() => {
    if (isCheckboxChecked && files.length > 0) {
      setFiles([]);
      setUploadedFiles([]);
      setTotalSize(0);
      setError(false);
    }
  }, [isCheckboxChecked, files.length, setFiles]);

  // Clear internal errors when submit is attempted (submitCount changes)
  // This prevents double error messages from showing simultaneously
  useEffect(() => {
    if (submitCount !== undefined && submitCount > 0) {
      setError(false);
      setErrorMessage('');
    }
  }, [submitCount]);

  const allowedExt = new Set([
    'png',
    'jpg',
    'jpeg',
    'webp',
    'gif',
    'bmp',
    'pdf',
    'heic',
    'heif',
  ]);

  //"manual" verification to prevent .heic bypassing the type check on windows
  function getExt(name: string) {
    const i = name.lastIndexOf('.');
    return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
  }

  const validator = (file: File): FileError | null => {
    if (preAuth) return null;

    const ext = getExt(file.name);

    // 1) Se o browser não dá mime (Windows/HEIC), usa extensão como fallback
    if (!file.type) {
      if (!allowedExt.has(ext)) {
        return {code: 'file-invalid-type', message: 'Tipo inválido'};
      }
      return null;
    }

    // 2) Com mime definido, aplica as regras normais
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/');
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif';

    if (!(isPdf || isImage || isHeic)) {
      return {code: 'file-invalid-type', message: 'Tipo inválido'};
    }

    return null;
  };

  const {
    open,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: preAuth ? '' : 'image/*, .pdf, .heic, .heif',
    validator: validator,
    maxSize: maxSize,
    disabled: isCheckboxChecked, // Disable dropzone when checkbox is checked
    onDropRejected: (fileRejectionArray: FileRejection[]) => {
      customOnDropBehavior && customOnDropBehavior();

      // Don't show errors if checkbox is checked
      if (isCheckboxChecked) return;

      let errorCodes: any[] = [];

      for (let i: number = 0; i < fileRejectionArray.length; i++) {
        const fileRejection = fileRejectionArray[i];
        errorCodes = errorCodes.concat(
          fileRejection.errors.map((error) => error.code),
        );
      }

      const filteredCodes = [...new Set(errorCodes)];

      if (filteredCodes.includes('file-invalid-type')) {
        setError(true);
        return setErrorMessage(labelsMessages.invalidFormat);
      }
      if (filteredCodes.includes('file-too-large')) {
        setError(true);
        return setErrorMessage(messagesLabels.document_sizeAlert);
      }
      if (filteredCodes.includes('files-too-large')) {
        setError(true);
        return setErrorMessage(messagesLabels.documents_sizeAlert);
      }
      // Verification of file name to not allow special character (currently disabled but leaving the code here just in case)
      // if (filteredCodes.includes('file-invalid-name')) {
      //   setError(true);
      //   return setErrorMessage(messagesLabels.invalid_file_name);
      // }
    },
    onDrop: async (acceptedFiles, rejectedFiles) => {
      customOnDropBehavior && customOnDropBehavior();
      // Don't process files if checkbox is checked
      if (isCheckboxChecked) return;
      let newFiles = [...files];
      let addedFilesId: any[] = [];

      // Verification of file name to not allow special character (currently disabled but leaving the code here just in case)
      // for (const f of acceptedFiles) {
      //   if (!isValidFileName(f.name)) {
      //     rejectedFiles.push({
      //       file: f,
      //       errors: [
      //         {code: 'file-invalid-name', message: 'Nome de ficheiro inválido'},
      //       ],
      //     });
      //     setError(true);
      //     setErrorMessage(messagesLabels.invalid_file_name);
      //     return; // interrompe se algum nome for inválido
      //   }
      // }

      if (filesSizeLimit) {
        const total = acceptedFiles.reduce(
          (acc, file) => acc + file.size,
          totalSize,
        );

        if (total > maxSize) {
          const rejectionError: FileRejection = {
            errors: [
              {
                code: 'files-too-large',
                message: messagesLabels.documents_sizeAlert,
              },
            ],
            file: null as unknown as File,
          };

          rejectedFiles.push(rejectionError);
          return;
        }

        setTotalSize(total);
      }

      await Promise.all(
        acceptedFiles.map((file: any, index: number) => {
          setError(false);
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => {
              let newFile = {
                file,
                ...file,
                preview: reader.result,
                id: createGUID(),
                name: newFilesName,
                path: file.name.replace(/ /g, ''),
              };

              addedFilesId.push(newFile.id);
              newFiles.push(newFile);
            };
            reader.readAsDataURL(file);
            reader.onloadend = () => resolve(reader.result);
          });
        }),
      );
      if (onFileAdd) {
        onFileAdd(addedFilesId);
      }
      setUploadedFiles(newFiles);
      setFiles(newFiles);
    },
  });

  const activeStyle = {
    borderColor: colors.black,
  };

  const acceptStyle = {
    borderColor: colors.fluorescentBlue,
  };

  const rejectStyle = {
    borderColor: colors.alertRed,
  };

  const style = useMemo(
    () => ({
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  const onDelete = (id: string) => {
    let newFiles = [...files];
    newFiles = newFiles.filter((elem) => {
      if (elem.id !== id) {
        return true;
      } else {
        URL.revokeObjectURL(elem.preview);
        let newTotalSize = totalSize - elem.file.size;
        setTotalSize(newTotalSize);
        return false;
      }
    });
    setUploadedFiles(newFiles);
    setFiles(newFiles);
    setError(false);
  };

  const renderCustomButton = () => {
    if (customResponsiveButton) {
      return (
        <>
          <Row onClick={() => open()}>
            {customResponsiveButton()}
            <input {...getInputProps()} />
          </Row>
        </>
      );
    } else {
      return (
        <AppButton
          title="Ok"
          className="responsive-button"
          variant="inverted"
          onClick={() => open()}>
          {createRefundLabels.createRefund_selectFilesButtonText}
          <input {...getInputProps()} />
        </AppButton>
      );
    }
  };

  const renderUploadedFiles = () =>
    uploadedFiles.map((file: any) => {
      return (
        <div className="app-drag-drop-preview-responsive">
          <AppFilePreview
            key={file.id}
            name={file.name}
            file={file}
            files={uploadedFiles}
            onDelete={() => onDelete(file.id)}
            scope="createRefund"
          />
        </div>
      );
    });

  const renderNoDocCheckbox = () => {
    return (
      <>
        {checkbox && (
          <div
            style={{
              textAlign: 'center',
            }}>
            <AppFormCheckboxField
              showErrorMessage={formik.submitCount > 0} // Show error message only when form is submitted
              style={{
                justifyContent: 'center',
                paddingTop: 30,
              }}
              name={name}
              label={
                <span style={{...fonts.SM, paddingTop: 2}}>
                  {createRefundLabels.createRefund_checkboxClinicDocuments}{' '}
                </span>
              }
            />
          </div>
        )}
        {checkboxPreRefund && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 15,
            }}>
            <AppFormCheckboxField
              showErrorMessage={formik.submitCount > 0} // Show error message only when form is submitted
              style={{
                justifyContent: 'center',
                paddingTop: 30,
              }}
              name={name}
              label={
                <span style={{...fonts.SM}}>
                  {
                    createRefundLabels.createRefund_preComparticipationAttachmentsCheckbox
                  }{' '}
                </span>
              }
            />
          </div>
        )}
      </>
    );
  };

  return (
    <React.Fragment>
      <Column className="app-drag-drop-container">
        {hasError ? (
          <NewErrorMessage
            text={errorMessage || messagesLabels.requiredField}
            iconName={Icons.ALERT_PROBLEM}
            colorIcon={colors.alertRed}
          />
        ) : (
          <></>
        )}

        {hidePreviews ? null : (
          <Row className="app-drag-drop-preview-row">
            {renderUploadedFiles()}
          </Row>
        )}

        <section className="container">
          <>
            {!isResponsive && (
              <div
                {...getRootProps({
                  className:
                    checkboxPreRefund || checkbox
                      ? 'dropzone-checkbox'
                      : 'dropzone',
                  style,
                })}
                onClick={open}>
                <Column className="app-drag-drop-file-input">
                  <Row className="drag-drop-image-container">
                    <img
                      className="app-drag-drop-icon"
                      src={attachfilesgrey}
                      style={{marginBottom: '15px', marginTop: '15px'}}
                    />
                    <div className="drag-drop-text">
                      <span style={{...fonts.M}}>
                        {`${createRefundLabels.createRefund_selectFilesText_browser_1} `}{' '}
                      </span>
                      <span
                        style={{
                          ...fonts.M,
                          color: globalStyles?.primaryColor || colors.blue,
                        }}>{`${createRefundLabels.createRefund_selectFilesText_browser_2}`}</span>
                    </div>
                  </Row>
                  <input {...getInputProps()} />

                  {renderNoDocCheckbox()}
                </Column>
              </div>
            )}
            {isResponsive && renderCustomButton()}
          </>
        </section>
      </Column>
    </React.Fragment>
  );
}

const mapStateToProps = ({strings, stringsDefault, globalStyles}: any) => ({
  labels: strings.value.pt.createRefund,
  labelsMessages: strings.value.pt.messages,
  globalStyles: globalStyles.value,
});

export default compose(connect(mapStateToProps)(AppDragDropFileInput));
